import { memo, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';

import { REQUESTS_TABS } from '~/constants';
import dwhExport, { DWH_EVENTS } from '~/dwhExport';
import useMount from '~/hooks/useMount';
import { hasPermission, PERMISSIONS } from '~/roles';
import { useAppDispatch, useAppSelector } from '~/store';
import { applicationsSelector } from '~/store/selectors/applicationsSelectors';
import { isClansFetchingSelector } from '~/store/selectors/clanSelectors';
import {
  currentAccountActiveApplicationsCountSelector,
  currentClanIdSelector,
  currentClanSelector,
  currentRoleNameSelector,
} from '~/store/selectors/currentAccountSelector';
import {
  acceptApplicationThunk,
  declineApplicationThunk,
  fetchApplicationsThunk,
} from '~/store/thunks/applicationsThunks';
import { actionsApplications } from '~/Actions/ActionApplications';
import { fetchClan } from '~/Actions/ActionClanProfile';

import headersAccountApplications from '~/Components/Requests/Applications/headers.account.settings.applications';
import headersClanApplications from '~/Components/Requests/Applications/headers.clan.settings.applications';
import Entries from '~/UIKit/Entries/Entries';
import StepCSSTransitionGroup from '~/UIKit/StepCSSTransitionGroup/StepCSSTransitionGroup';

import type { IClanApplication } from '~/Actions/ActionApplications';

const RequestsApplications = () => {
  const { animationLevelNested } = useOutletContext<{ animationLevelNested: number }>();
  const dispatch = useAppDispatch();

  const currentActiveApplicationsCount = useAppSelector(currentAccountActiveApplicationsCountSelector);
  const currentRoleName = useAppSelector(currentRoleNameSelector);
  const currentClanId = useAppSelector(currentClanIdSelector);
  const isClansFetching = useAppSelector(isClansFetchingSelector);
  const applications = useAppSelector(applicationsSelector);
  const currentClan = useAppSelector(currentClanSelector);

  useMount(() => {
    const canHandleInvites = hasPermission(currentRoleName, PERMISSIONS.HANDLE_INVITES);
    if (canHandleInvites) {
      dwhExport.push(DWH_EVENTS.REQUESTS.CLAN.VIEW_REQUESTS);
    } else {
      dwhExport.push(DWH_EVENTS.REQUESTS.PLAYER.VIEW_REQUESTS);
    }

    if (currentClanId && !currentClan && !isClansFetching) {
      void dispatch(fetchClan(currentClanId));
    }
  });

  const activeApplicationsCount = useRef(currentActiveApplicationsCount);

  // Update list on accountSync new data
  useEffect(() => {
    if (
      currentClanId &&
      activeApplicationsCount.current !== currentActiveApplicationsCount &&
      !applications.isFetching
    ) {
      activeApplicationsCount.current = currentActiveApplicationsCount;
      void dispatch(fetchApplicationsThunk());
    }
  }, [currentActiveApplicationsCount, currentClanId, dispatch, applications.isFetching]);

  const fetchEntriesByCurrentState = (withGlobalSpinner: boolean) => {
    void dispatch(fetchApplicationsThunk(withGlobalSpinner));
  };
  const fetchEntriesByPage = (page: number) => {
    dispatch(actionsApplications.changePage(page));
    void dispatch(fetchApplicationsThunk());
    dispatch(actionsApplications.toggleAllApplicationsPlayersTick());
  };

  const onAcceptedEntry = (application: IClanApplication) => {
    void dispatch(acceptApplicationThunk(application));
  };
  const onDeclinedEntry = (application: IClanApplication) => {
    void dispatch(declineApplicationThunk(application));
  };

  const onReloadClick = () => {
    void dispatch(fetchApplicationsThunk());
  };
  const onSortClick = (order: string, isAsc: boolean) => {
    dispatch(actionsApplications.changeOrder(order, isAsc));
    void dispatch(fetchApplicationsThunk());
  };

  if (currentClanId) {
    return (
      <StepCSSTransitionGroup level={animationLevelNested}>
        <Entries
          isOperational={true}
          entries={applications}
          entryType={REQUESTS_TABS.APPLICATION}
          headers={headersAccountApplications}
          fetchEntriesByCurrentState={fetchEntriesByCurrentState}
          fetchEntriesByPage={fetchEntriesByPage}
          onAcceptedEntry={onAcceptedEntry}
          onDeclinedEntry={onDeclinedEntry}
          onReloadClick={onReloadClick}
          onSortClick={onSortClick}
        />
      </StepCSSTransitionGroup>
    );
  }

  return (
    <StepCSSTransitionGroup level={animationLevelNested}>
      <Entries
        isOperational={false}
        entries={applications}
        entryType={REQUESTS_TABS.APPLICATION}
        headers={headersClanApplications}
        fetchEntriesByCurrentState={fetchEntriesByCurrentState}
        fetchEntriesByPage={fetchEntriesByPage}
        onReloadClick={onReloadClick}
        onSortClick={onSortClick}
      />
    </StepCSSTransitionGroup>
  );
};

export default memo(RequestsApplications);
